/**
 * Durcit le DOM contre les extensions de traduction du navigateur (Google Translate…).
 *
 * Ces extensions remplacent des nœuds texte par leur traduction. React garde une référence vers les
 * nœuds d'origine et, au démontage, appelle `removeChild` / `insertBefore` sur des nœuds que la
 * traduction a déplacés → `NotFoundError: Failed to execute 'removeChild' on 'Node'`. C'est de loin
 * le 1er crash front en prod (≈ 86 % des [ErrorBoundary]).
 *
 * On rend ces deux opérations tolérantes : si le nœud n'est plus l'enfant attendu, on dégrade au
 * lieu de jeter, et React considère le (dé)montage comme réussi. Workaround standard et documenté,
 * cf. https://github.com/facebook/react/issues/11538
 */
type HardenedMethod = { __hardenedForTranslation?: true };

export function hardenDomForTranslation(): void {
    if (typeof Node !== 'function' || !Node.prototype) {
        return;
    }

    // Idempotence : si `removeChild` porte déjà notre marque, le DOM est patché — on n'empile pas un
    // second wrapper (HMR, double-wire, tests). On marque la méthode patchée plutôt qu'un flag de
    // module, pour rester correct même si le prototype est réinitialisé ailleurs.
    if ((Node.prototype.removeChild as unknown as HardenedMethod).__hardenedForTranslation) {
        return;
    }

    // eslint-disable-next-line @typescript-eslint/unbound-method -- méthode native réinvoquée plus bas avec un `this` explicite
    const originalRemoveChild = Node.prototype.removeChild;
    const patchedRemoveChild = function removeChild<T extends Node>(this: Node, child: T): T {
        const currentParent = child.parentNode as Node | null;
        if (currentParent !== this) {
            // La traduction a déplacé le nœud : on le retire de son vrai parent si possible.
            currentParent?.removeChild(child);
            return child;
        }
        return originalRemoveChild.call(this, child) as T;
    };
    (patchedRemoveChild as unknown as HardenedMethod).__hardenedForTranslation = true;
    Node.prototype.removeChild = patchedRemoveChild;

    // eslint-disable-next-line @typescript-eslint/unbound-method -- méthode native réinvoquée plus bas avec un `this` explicite
    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function insertBefore<T extends Node>(
        this: Node,
        newNode: T,
        referenceNode: Node | null,
    ): T {
        const referenceParent =
            referenceNode === null ? this : (referenceNode.parentNode as Node | null);
        if (referenceParent !== this) {
            // Le nœud de référence n'est plus ici : on insère en fin plutôt que de jeter.
            return originalInsertBefore.call(this, newNode, null) as T;
        }
        return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };
}
